import { useEffect, useState } from "react";
import filteringProps from "../filtering/cancelreasons.json";
import sortingProps from "../sorting/cancelreasons.json";
import { DataTable, DataTableField } from "../../../../components/data/DataTable";
import DataTableActiveToggle from "../../../../components/data/DataTableActiveToggle";
import { API_ROUTES } from '../../../../core/Constants';
import { getUser, hasRole, setPage, toPrettyDate, useHub } from '../../../../core/Global';
import Modal from "../../../../components/base/Modal";
import { DrawerForm } from "../../../../components/drawers/DrawerForm";
import { toastSuccess } from "../../../../components/base/Toast";
import Icon from "../../../../components/base/Icon";
import FlexRow from "../../../../components/base/FlexRow";
import TextBox from "../../../../components/form/TextBox";
import CancelReasonItem from "../models/CancelReasonItem";

/** Managing the list is open to the owner and system admins, and role-gated for everyone else. */
const canManageReasons = () => {
  const user = getUser()
  if (!user) return false
  if (user.isSuperAdmin || user.isSystemAdmin || user.isCustomer) return true
  return hasRole("ManageCancelReasons")
}

const canDeleteReasons = () => {
  const user = getUser()
  if (!user) return false
  if (user.isSuperAdmin || user.isSystemAdmin || user.isCustomer) return true
  return hasRole("DeleteCancelReasons")
}

export default function CancelReasonsList() {

  const hub = useHub();

  const [createModal, createModalSet] = useState<boolean>(false);
  const [editModal, editModalSet] = useState<boolean>(false);
  const [deleteModal, deleteModalSet] = useState<boolean>(false);
  const [selectedItem, selectedItemSet] = useState<CancelReasonItem>();

  // Asked for when the delete dialog opens, so it can say how many jobs quote this reason
  // (undefined while the count is still in flight).
  const [deleteJobsCount, deleteJobsCountSet] = useState<number>();

  const [refresh, refreshSet] = useState<boolean>();
  const [clear, clearSet] = useState<boolean>();

  useEffect(() => {
    setPage("Cancel Reasons", "CRM", "Base Data");
  }, [])

  useEffect(() => {
    if (refresh) setTimeout(() => refreshSet(false), 1000);
    if (clear) setTimeout(() => clearSet(false), 1000);
  }, [refresh, clear])

  const canManage = canManageReasons();

  const edit = (data: CancelReasonItem) => {
    selectedItemSet(data);
    editModalSet(true);
  }

  const deleteReason = (data: CancelReasonItem) => {
    selectedItemSet(data);
    deleteJobsCountSet(undefined);
    deleteModalSet(true);
    hub.Get(API_ROUTES.CancelReasons + "jobscount/" + data?.id, res => {
      if (res.success) deleteJobsCountSet(Number.parseInt(res.data.jobsCount) || 0);
    })
  }

  const NameItem = (data: CancelReasonItem) => (
    <FlexRow>
      <Icon icon="ban" type="solid" size={12} className="text-gray-500 me-3" />
      <div>
        {data.name}
      </div>
      {
        canManage &&
        <a href="#" className="ms-1"
          onClick={e => {
            e.preventDefault();
            edit(data);
          }}>
          <Icon icon="edit" className="ms-2" size={12} />
        </a>
      }
    </FlexRow>
  )

  const CreatedItem = (data: CancelReasonItem) => (
    <div>
      <div className='d-flex align-items-center'>
        {toPrettyDate(data.createdAt)}
      </div>
      {
        data.creatorUser &&
        <FlexRow className='fs-8 text-gray-600 mt-1'>
          <Icon icon="user" className="me-2" size={10} type="solid" />
          <div className="fs-8">
            By: {data.creatorUser.fullName}
          </div>
        </FlexRow>
      }
    </div>
  )

  return (
    <>
      <DataTable
        objectName="Cancel Reason"
        apiRoute={API_ROUTES.CancelReasons}
        filteringProps={filteringProps}
        sortingProps={sortingProps}
        // The order set here is the order the cancel form lists them in, so the table has to read
        // the same way round — without this it asks for descending and the two disagree.
        ascending
        reorder={canManage}
        onReorder={ids => {
          hub.Post(API_ROUTES.CancelReasons + "reorder", ids, res => { })
        }}
        addCustom={canManage ? () => createModalSet(true) : undefined}
        edit={canManage} editCustom={edit}
        delete={canDeleteReasons()} deleteCustom={deleteReason}
        refresh={refresh}>
        <DataTableField title="" width={50}>
          {/* Off means the reason is no longer offered on the cancel form, while the jobs already
              cancelled with it are untouched. */}
          <DataTableActiveToggle route={API_ROUTES.CancelReasons} />
        </DataTableField>
        <DataTableField title="Reason">
          <NameItem />
        </DataTableField>
        <DataTableField title="Created">
          <CreatedItem />
        </DataTableField>
      </DataTable>

      {
        createModal &&
        <DrawerForm id="drawer-add-cancelreason" permanent size="medium"
          title="Create Cancel Reason"
          buttonText="Add Reason"
          onOpen={() => $("#txt-add-cancelreason").focus()}
          onClose={() => createModalSet(false)}
          onSubmit={(body, callback) => {

            if (window.isNullOrEmpty(body.name) || body.name.trim().length < 2) {
              window.formError("name", "Please write a valid reason!", "drawer-add-cancelreason", true);
              if (callback) callback();
              return;
            }

            hub.Post(API_ROUTES.CancelReasons, body, res => {
              if (res.success) {
                refreshSet(true);
                clearSet(true);
                toastSuccess("Cancel reason has been added successfully!");
              }
              if (callback) callback(res);
            })

          }}>
          <div>
            <TextBox id="txt-add-cancelreason" name="name" block label="Reason*" labelUp
              placeholder="e.g. Customer was not home" clear={clear} />
            <div className="fs-8 text-muted mt-2">
              Write it the way it should read on the job — this is exactly what the technician picks
              and what the job then shows as its cancellation note.
            </div>
          </div>
        </DrawerForm>
      }

      {
        editModal && selectedItem &&
        <DrawerForm id="drawer-edit-cancelreason" permanent size="medium"
          title="Edit Cancel Reason"
          buttonText="Update Reason"
          onOpen={() => $("#txt-edit-cancelreason").focus()}
          onClose={() => {
            editModalSet(false);
            selectedItemSet(undefined);
          }}
          onSubmit={(body, callback) => {

            if (window.isNullOrEmpty(body.name) || body.name.trim().length < 2) {
              window.formError("name", "Please write a valid reason!", "drawer-edit-cancelreason", true);
              if (callback) callback();
              return;
            }

            hub.Put(API_ROUTES.CancelReasons + selectedItem.id, body, res => {
              if (res.success) {
                refreshSet(true);
                toastSuccess("Cancel reason has been updated successfully!");
              }
              if (callback) callback(res);
            })

          }}>
          <div>
            <TextBox id="txt-edit-cancelreason" name="name" block label="Reason*" labelUp
              value={selectedItem.name} />
            <div className="fs-8 text-muted mt-2">
              Rewording this also corrects it on every job that was already cancelled with it.
            </div>
          </div>
        </DrawerForm>
      }

      {
        deleteModal && selectedItem &&
        <Modal id="modal-delete-cancelreason"
          title="Delete Cancel Reason"
          buttonText="Delete Reason"
          buttonColor="danger"
          open visible staticBackdrop
          onClose={() => {
            deleteModalSet(false);
            selectedItemSet(undefined);
          }}
          onSubmit={(body, callback) => {

            hub.Delete(API_ROUTES.CancelReasons + selectedItem.id, res => {
              if (res.success) {
                refreshSet(true);
                toastSuccess("Cancel reason has been deleted successfully!");
              }
              if (callback) callback(res);
            })

          }}
          secondActionColor="secondary"
          secondActionText="Disable Instead"
          onSecondAction={() => {
            hub.Post(API_ROUTES.CancelReasons + "activetoggle", {
              id: selectedItem.id,
              active: "0"
            }, res => {
              refreshSet(true);
              window.closeModal("modal-delete-cancelreason");
            })
          }}>
          <div>
            <div className="mb-4">
              Are you sure you want to delete <span className="fw-bold">{selectedItem.name}</span>?
            </div>
            {
              deleteJobsCount == undefined ?
                <div className="text-gray-600">
                  <Icon icon="spinner-third" spin />
                  <span className="ms-2">Checking how many jobs were canceled with it...</span>
                </div>
                : deleteJobsCount > 0 ?
                  <div className="text-gray-700">
                    {deleteJobsCount} {deleteJobsCount > 1 ? "jobs were" : "job was"} canceled with
                    this reason. {deleteJobsCount > 1 ? "They" : "It"} keep{deleteJobsCount > 1 ? "" : "s"} the
                    wording — deleting only takes the reason off the cancel form.
                  </div>
                  :
                  <div className="text-gray-700">
                    It will no longer be offered on the cancel form.
                  </div>
            }
          </div>
        </Modal>
      }
    </>
  );
}
